import {
  BarChartOutlined,
  CalendarOutlined,
  DashboardOutlined,
  FileTextOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { AppBar } from '@horof/shared';
import type { ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  NavLink,
  Outlet,
  ScrollRestoration,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { BillPage } from './bills/BillPage';
import { DocumentPage } from './documents/DocumentPage';
import { DocumentsPage } from './documents/DocumentsPage';
import { useSeesDocuments } from './documents/documents-data';
import { ScanPage } from './documents/ScanPage';
import { HELP } from './help';
import { MovedPage } from './MovedPage';
import { MemberPage } from './members/MemberPage';
import { MembersPage } from './members/MembersPage';
import { MeterPage } from './meters/MeterPage';
import { MetersPage } from './meters/MetersPage';
import { NewMeterPage } from './meters/NewMeterPage';
import { MonthPage } from './month/MonthPage';
import { ArrearsPage } from './reports/ArrearsPage';
import { RemindersPage } from './reports/RemindersPage';
import { ReportsPage } from './reports/ReportsPage';

const SECTIONS: { to: string; label: string; icon: ReactNode }[] = [
  { to: '/month', label: 'الشهر', icon: <CalendarOutlined /> },
  { to: '/members', label: 'الأعضاء', icon: <TeamOutlined /> },
  { to: '/meters', label: 'العدادات', icon: <DashboardOutlined /> },
  { to: '/reports', label: 'التقارير', icon: <BarChartOutlined /> },
  // The bureau only (arqam doc 52): hidden from others.
  { to: '/documents', label: 'الوثائق', icon: <FileTextOutlined /> },
];

function Layout() {
  const seesDocuments = useSeesDocuments();
  return (
    <>
      <ScrollRestoration />
      <AppBar help={HELP} />
      <div className="with-nav">
        <Outlet />
      </div>
      <nav className="bottom-nav">
        {SECTIONS.filter((s) => s.to !== '/documents' || seesDocuments).map(
          (s) => (
            <NavLink key={s.to} to={s.to}>
              {s.icon}
              <span>{s.label}</span>
            </NavLink>
          ),
        )}
      </nav>
    </>
  );
}

const router = createBrowserRouter(
  [
    {
      element: <Layout />,
      children: [
        { path: '/', element: <Navigate to="/month" replace /> },
        { path: '/month', element: <MonthPage /> },
        { path: '/members', element: <MembersPage /> },
        // Editing members moved to the desk (docs/changes/41–42).
        { path: '/members/new', element: <MovedPage what="إضافة الأعضاء" /> },
        {
          path: '/members/committee',
          element: <MovedPage what="أعضاء اللجنة" />,
        },
        { path: '/members/:memberId', element: <MemberPage /> },
        { path: '/meters', element: <MetersPage /> },
        { path: '/meters/new', element: <NewMeterPage /> },
        { path: '/meters/:meterId', element: <MeterPage /> },
        { path: '/bills/:billId', element: <BillPage /> },
        // Prices and settings moved to the desk (docs/changes/39–40).
        { path: '/pricing/*', element: <MovedPage what="الأسعار" /> },
        { path: '/settings', element: <MovedPage what="الإعدادات" /> },
        { path: '/reports', element: <ReportsPage /> },
        { path: '/documents', element: <DocumentsPage /> },
        { path: '/documents/scan', element: <ScanPage /> },
        { path: '/documents/:documentId', element: <DocumentPage /> },
        { path: '/reports/reminders', element: <RemindersPage /> },
        { path: '/reports/arrears', element: <ArrearsPage /> },
      ],
    },
  ],
  { basename: import.meta.env.BASE_URL },
);

export function App() {
  return <RouterProvider router={router} />;
}
